import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import CourseCatalog from "../training-selection/course-catalog";
import ConfigureTrainingModal from "../training-selection/configure-training-modal";
import "./ai-training-selection.css";

/**
 * Course picker for the AI/MCP chat campaign flow.
 *
 * Rendered in the chat when the campaign-run sequence reaches the training step
 * (Simulation Attack + Training only). Uses the same card grid and "Configure
 * Training" modal as the web launch wizard, then hands the finished training
 * object back to the chat via `onDone` so it can be attached to the campaign
 * draft — courses are chosen at campaign time, not baked into the template.
 */
const AiTrainingSelection = ({
	onDone,
	disabled = false,
	initialTraining = null,
	doneLabel = "\u2713 Done",
	lockAfterDone = true,
}) => {
	const initial = initialTraining || {};
	const [trainingAssignmentMode, setTrainingAssignmentMode] = useState(
		initial.trainingAssignmentMode === "multiple" ? "multiple" : "single"
	);
	const [selectedCourses, setSelectedCourses] = useState(
		Array.isArray(initial.selectedCourses) ? [...initial.selectedCourses] : []
	);
	const [options, setOptions] = useState(initial.selectedOptions ? { ...initial.selectedOptions } : {});
	const [courseName, setCourseName] = useState(initial.courseName ? { ...initial.courseName } : {});
	const [imageURL, setImageURL] = useState(initial.imageURL || "");
	const [templateIds, setTemplateIds] = useState({
		sendCourseTemplateId: initial.sendCourseTemplateId || "",
		courseCompletionTemplateId: initial.courseCompletionTemplateId || "",
		courseQuizFailReminderTemplateId: initial.courseQuizFailReminderTemplateId || "",
	});
	const [showConfigureModal, setShowConfigureModal] = useState(false);
	const [configureTab, setConfigureTab] = useState("course");
	/* Re-opening an existing selection has already been through the email
	   template step once, so don't force the user back through it. */
	const [emailTemplateTabVisited, setEmailTemplateTabVisited] = useState(
		!!(initialTraining && Array.isArray(initialTraining.selectedCourses) && initialTraining.selectedCourses.length)
	);
	const [submitted, setSubmitted] = useState(false);

	const isLocked = disabled || submitted;

	const handleCoursesLoaded = useCallback(({ courseName: names, imageURL: url }) => {
		setCourseName((prev) => ({ ...prev, ...names }));
		if (url) setImageURL(url);
	}, []);

	const handleModeChange = (mode) => {
		if (isLocked) return;
		setTrainingAssignmentMode(mode);
		// Switching back to Single keeps only the first course.
		setSelectedCourses((prev) => (mode === "single" && prev.length > 1 ? [prev[0]] : prev));
	};

	const handleCourseToggle = (courseId) => {
		if (isLocked) return;
		setSelectedCourses((prev) => {
			const idx = prev.indexOf(courseId);
			if (idx > -1) return prev.filter((id) => id !== courseId);
			if (trainingAssignmentMode === "single") return [courseId];
			return [...prev, courseId];
		});
	};

	const handleCourseRemove = (courseId) => {
		setSelectedCourses((prev) => prev.filter((id) => id !== courseId));
	};

	const handleSetCourseOptions = (courseId, settings) => {
		setOptions((prev) => ({ ...prev, [courseId]: settings }));
	};

	const handleTemplateIdChange = (key, value) => {
		setTemplateIds((prev) => ({ ...prev, [key]: value }));
	};

	/* Build the campaign-level training object the launch payload expects
	   (backend simulationOneSchema.training).
	   Only genuinely-configured courses go into `selectedOptions` — defaulting every
	   *selected* course here (via buildSelectedOptions) would make this component, on
	   remount with this same object as `initialTraining` (e.g. reopening "Change
	   Courses" on the campaign draft preview), treat a merely-selected course as
	   already configured. Callers that need every selected course to have settings
	   at send time (the chat POST, the draft's launch payload) fill in the defaults
	   themselves right before sending — see mcp.js's handleTrainingSelected and
	   campaign-draft-preview.js's buildTrainingLaunchPayload. */
	const buildTraining = () => ({
		trainingAssignmentMode,
		selectedCourses: [...selectedCourses],
		selectedOptions: { ...options },
		courseName,
		imageURL,
		sendCourseTemplateId: templateIds.sendCourseTemplateId || null,
		courseCompletionTemplateId: templateIds.courseCompletionTemplateId || null,
		courseQuizFailReminderTemplateId: templateIds.courseQuizFailReminderTemplateId || null,
		sendQuizTemplateId: null,
	});

	const finish = () => {
		if (lockAfterDone) setSubmitted(true);
		if (onDone) onDone(buildTraining());
	};

	/* Same gate as the web wizard's Continue button
	   (launch-new-modal.js isTrainingContinueEnabled): Single needs one course,
	   Multiple needs at least two. With no courses available at all — no TLMS on
	   the plan, expired licence, or an empty catalogue — this stays disabled and
	   the campaign cannot proceed, exactly as the wizard behaves.

	   Unlike the wizard it does not force a trip through the Configure Training
	   modal: courses left unconfigured launch with the shared defaults and stay
	   editable in the campaign launch preview. */
	const isSelectionValid =
		trainingAssignmentMode === "multiple" ? selectedCourses.length > 1 : selectedCourses.length >= 1;

	const handleDone = () => {
		if (isLocked) return;
		if (!selectedCourses.length) {
			toast.error("Please select at least one training course");
			return;
		}
		if (trainingAssignmentMode === "multiple" && selectedCourses.length < 2) {
			toast.error("Multiple mode requires at least two training courses");
			return;
		}
		if (trainingAssignmentMode === "single" && selectedCourses.length > 1) {
			toast.error("Single mode allows only one training course");
			return;
		}
		finish();
	};

	const handleModalSave = () => {
		setShowConfigureModal(false);
		finish();
	};

	const openConfigureModal = () => {
		if (!selectedCourses.length) {
			toast.error("Please select at least one training course first");
			return;
		}
		setConfigureTab("course");
		setShowConfigureModal(true);
	};

	return (
		<div className="ats-wrapper">
			{/* <p className="ats-title">Which training would you like to assign to the targeted users?</p> */}

			<div className="ats-catalog">
				<CourseCatalog
					selectedCourses={selectedCourses}
					trainingAssignmentMode={trainingAssignmentMode}
					onModeChange={handleModeChange}
					onToggleCourse={handleCourseToggle}
					onCoursesLoaded={handleCoursesLoaded}
					disabled={isLocked}
				/>
			</div>

			<div className="ats-footer">
				<div className="ats-footer-left">
					{/* <button
						type="button"
						className="ats-done-btn"
						onClick={handleDone}
						disabled={isLocked || !isSelectionValid}
						title={
							isSelectionValid
								? undefined
								: trainingAssignmentMode === "multiple"
									? "Select at least two training courses"
									: "Select a training course"
						}
					>
						{doneLabel}
					</button> */}
					{isLocked || isSelectionValid ? (
						<button
							type="button"
							className="ats-configure-btn"
							onClick={openConfigureModal}
							disabled={isLocked}
						>
							Configure Training
						</button>
					) : (
						/* Disabled buttons swallow mouse events, so the span is the hover target. */
						<OverlayTrigger
							placement="top"
							delay={{ show: 0, hide: 0 }}
							overlay={
								<Tooltip id="ats-configure-disabled">
									{trainingAssignmentMode === "multiple"
										? "Select at least two training courses"
										: "Select a training course"}
								</Tooltip>
							}
						>
							<span className="d-inline-block" style={{ cursor: "not-allowed" }}>
								<button
									type="button"
									className="ats-configure-btn"
									disabled
									style={{ pointerEvents: "none" }}
								>
									Configure Training
								</button>
							</span>
						</OverlayTrigger>
					)}
				</div>
				<span className="ats-selected-count">
					{selectedCourses.length
						? `${selectedCourses.length} course${selectedCourses.length > 1 ? "s" : ""} selected`
						: "No courses selected"}
				</span>
			</div>

			<ConfigureTrainingModal
				show={showConfigureModal}
				onHide={() => setShowConfigureModal(false)}
				initialTab={configureTab}
				selectedCourses={selectedCourses}
				options={options}
				courseName={courseName}
				imageURL={imageURL}
				templateIds={templateIds}
				emailTemplateTabVisited={emailTemplateTabVisited}
				onEmailTabVisited={() => setEmailTemplateTabVisited(true)}
				onTemplateIdChange={handleTemplateIdChange}
				onRemoveCourse={handleCourseRemove}
				onSetCourseOptions={handleSetCourseOptions}
				onSave={handleModalSave}
			/>
		</div>
	);
};

export default AiTrainingSelection;
